import React, { SyntheticEvent, useMemo, useState } from 'react';

import { GoogleMap, StreetViewPanorama } from '@react-google-maps/api';
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import cn from 'classnames';
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import { useLocalStorage } from 'usehooks-ts';

import { Polyline } from './Polyline';

import collapseIcon from 'assets/images/collapse.svg';
import expandIcon from 'assets/images/expand.svg';
import mapDirectionImage from 'assets/images/inspection/map-direction.svg';
import mapIcon from 'assets/images/map.svg';
import { ReturnComponentType } from 'common';
import { MapSize } from 'enums';
import { useAppSelector, useGoogleMaps } from 'hooks';
import { VideoPlayerHookType } from 'hooks/useVideoPlayer/useVideoPlayer';
import { TimeCodeType, selectFiles } from 'state';
import './InspectionMaps.scss';
import 'react-resizable/css/styles.css';
import { Segment } from 'ui-kit';
import { getMetadataAtTime } from 'utils';

type InspectionMapsType = {
  focusedTimeCode?: TimeCodeType;
  videoPlayer: VideoPlayerHookType;
};

type Point = {
  lat: number;
  lng: number;
};

export const InspectionMaps = ({
  focusedTimeCode,
  videoPlayer,
}: InspectionMapsType): ReturnComponentType => {
  const files = useAppSelector(selectFiles);
  const { isLoaded } = useGoogleMaps();
  const [mapSize, setMapSize] = useLocalStorage('map:mapSize', MapSize.SMALL);
  const [smallMapDimensions, setSmallMapDimensions] = useLocalStorage(
    'map:smallMapDimensions',
    { width: 200, height: 200 },
  );
  const { currentFile, fileProgress } = videoPlayer;

  const [heading, setHeading] = useState<number>();
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [isStreetViewHidden, setIsStreetViewHidden] = useLocalStorage(
    'map:isStreetViewHidden',
    false,
  );

  const [streetShowAddress, setStreetShowAddress] = useLocalStorage(
    'map:streetShowAddress',
    false,
  );

  const streetViewLocation = useMemo(() => {
    if (streetShowAddress && focusedTimeCode && focusedTimeCode.property) {
      return {
        lat: focusedTimeCode.property.latitude,
        lng: focusedTimeCode.property.longitude,
      };
    }
    if (!streetShowAddress) {
      if (!currentFile || !currentFile.metadata) return;
      const metaData = getMetadataAtTime(currentFile.metadata, fileProgress);

      if (!metaData) return;

      return {
        lat: metaData.latitude,
        lng: metaData.longitude,
      };
    }
  }, [
    fileProgress,
    currentFile,
    currentFile?.metadata,
    streetShowAddress,
    focusedTimeCode,
  ]);

  const mapLocation = useMemo(() => {
    if (!currentFile || !currentFile.metadata) return;
    const metaData = getMetadataAtTime(currentFile.metadata, fileProgress);

    if (!metaData) return;

    const metaDataIndex = currentFile.metadata.indexOf(metaData);
    const carLastPosition =
      metaDataIndex > 0 ? currentFile.metadata[metaDataIndex - 1] : undefined;

    const metaDataLatLng = {
      lat: metaData.latitude,
      lng: metaData.longitude,
    };

    const carHeading = carLastPosition
      ? window.google.maps.geometry?.spherical.computeHeading(metaDataLatLng, {
          lat: carLastPosition.latitude,
          lng: carLastPosition.longitude,
        })
      : undefined;

    setHeading(carHeading);

    return metaDataLatLng;
  }, [
    fileProgress,
    currentFile,
    currentFile?.metadata,
    streetShowAddress,
    focusedTimeCode,
  ]);

  const icons = [
    {
      icon: {
        // arrow:
        path: 'M -1,1 0,-1 1,1 Z',
        scale: 6,
        strokeWeight: 0,
        fillOpacity: 1,
        fillColor: '#4f72ee',
      },
      offset: '0',
      repeat: '80px',
    },
  ];

  const map = useMemo(() => {
    return (
      <>
        <Segment className="StreetToggle">
          {!isStreetViewHidden && (
            <>
              <button
                type="button"
                aria-selected={!streetShowAddress}
                role="tab"
                onClick={() => setStreetShowAddress(false)}
              >
                Follow Car
              </button>
              <button
                type="button"
                aria-selected={streetShowAddress}
                role="tab"
                onClick={() => setStreetShowAddress(true)}
              >
                Follow Address
              </button>
            </>
          )}
          {mapSize === MapSize.LARGE && (
            <button
              type="button"
              aria-selected={false}
              role="tab"
              onClick={() => setIsStreetViewHidden(!isStreetViewHidden)}
            >
              {isStreetViewHidden ? 'Show' : 'Hide'} Street View
            </button>
          )}
        </Segment>
        {!isStreetViewHidden && (
          <GoogleMap mapContainerClassName="StreetMap">
            <StreetViewPanorama
              options={{
                position: streetViewLocation,
                visible: true,
                disableDefaultUI: true,
                enableCloseButton: false,
              }}
            />
          </GoogleMap>
        )}
        <APIProvider apiKey="" libraries={['maps', 'geometry']}>
          <Map
            mapId="street-map"
            className={`GoogleMap ${isStreetViewHidden ? 'Full' : ''}`}
            defaultCenter={{ lat: 37.7749, lng: -122.4194 }}
            center={mapLocation}
            defaultZoom={19}
            gestureHandling="greedy"
            disableDefaultUI
            mapTypeId={mapTypeId}
            keyboardShortcuts={false}
          >
            {/* do not combine lines from one file to the next, in case they are far apart */}
            {files.map(file => {
              const path: Point[] | undefined = file?.metadata?.map(p => {
                return {
                  lat: p.latitude,
                  lng: p.longitude,
                };
              });

              return (
                <Polyline
                  key={`polyline-${file.id}`}
                  path={path || []}
                  strokeColor="#4f72ee"
                  strokeOpacity={0.7}
                  strokeWeight={5}
                  icons={icons}
                />
              );
            })}
            <AdvancedMarker className="GoogleMap_Marker" position={mapLocation}>
              <div className="GoogleMap_Marker_Dot" />
              {heading && (
                <img
                  src={mapDirectionImage}
                  className="GoogleMap_Marker_Direction"
                  alt=""
                  style={{
                    transform: `translate(-50%, -50%) rotate(${heading}deg)`,
                  }}
                />
              )}
            </AdvancedMarker>
            <button
              className="GoogleMap_Toggle"
              type="button"
              onClick={() =>
                setMapTypeId(mapTypeId === 'roadmap' ? 'satellite' : 'roadmap')
              }
            >
              {mapTypeId === 'roadmap' ? 'Satellite' : 'Road map'}
            </button>
          </Map>
        </APIProvider>
      </>
    );
  }, [
    currentFile,
    mapLocation,
    mapTypeId,
    mapSize,
    isStreetViewHidden,
    streetShowAddress,
  ]);

  if (mapSize === MapSize.HIDDEN) {
    return (
      <button
        className="Maps MapsHidden Button"
        type="button"
        onClick={() => setMapSize(MapSize.SMALL)}
      >
        <img src={mapIcon} alt="" />
        <br />
        View map
        {/* ↖&nbsp;Show map */}
      </button>
    );
  }

  const onSmallMapResize = (e: SyntheticEvent, data: ResizeCallbackData) => {
    setSmallMapDimensions({ width: data.size.width, height: data.size.height });
  };

  if (mapSize === MapSize.SMALL) {
    return (
      <ResizableBox
        width={smallMapDimensions.width}
        height={smallMapDimensions.height}
        minConstraints={[200, 150]}
        maxConstraints={[600, 500]}
        resizeHandles={['w', 'nw', 'n']}
        className="Maps MapsSmall"
        onResizeStop={onSmallMapResize}
      >
        <>
          {isLoaded && map}

          <div className="MapsSmall_Buttons">
            <button
              className="Button"
              type="button"
              onClick={() => setMapSize(MapSize.LARGE)}
            >
              <img src={expandIcon} alt="Expand map" />
            </button>
            <button
              className="Button"
              type="button"
              onClick={() => setMapSize(MapSize.HIDDEN)}
            >
              <img src={collapseIcon} alt="Hide map" />
            </button>
          </div>
        </>
      </ResizableBox>
    );
  }

  return (
    <div className={cn('Maps', mapSize === MapSize.LARGE && 'MapsLarge')}>
      {isLoaded && map}
      <div className="MapsLarge_Buttons">
        <button
          className="Button"
          type="button"
          onClick={() => {
            setIsStreetViewHidden(false);
            setMapSize(MapSize.SMALL);
          }}
        >
          <img src={collapseIcon} alt="Shrink map" />
        </button>
      </div>
    </div>
  );
};
